/**
 * 认证相关 Store
 * 负责用户登录、认证状态管理、验证码倒计时等功能
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useCacheStore } from '../cache'
import { cacheKeys } from '../config'
import { authApi } from '@/https/api/auth'
import { userApi } from '@/https/api/user'
import type {
    UserLoginRequest,
    SMSLoginRequest,
    PasswordResetRequest,
    SendCodeResponse
} from '@/https/types/auth'
import type { UserDetail, UserUpdateRequest } from '@/https/types/user'

export const useAuthStore = defineStore('auth', () => {
    const cacheStore = useCacheStore()

    // ==================== 状态管理 ====================

    // JWT认证令牌
    const accessToken = ref<string | null>(null)

    // 当前用户信息
    const currentUser = ref<UserDetail | null>(null)

    // 登录加载状态
    const loginLoading = ref(false)

    // 认证错误信息
    const authError = ref<string | null>(null)

    // ==================== 计算属性 ====================

    // 是否已认证
    const isAuthenticated = computed(() => {
        return !!accessToken.value && !!currentUser.value
    })

    // 是否可以访问管理后台（仅基于后台访问标识或管理员身份）
    const canAccessAdmin = computed(() => {
        const user = currentUser.value as (UserDetail & { is_admin?: boolean }) | null
        if (!user) return false
        return user.can_access_admin === true || user.is_admin === true
    })

    // 是否可以访问代理商页面（基于代理商身份）
    const canAccessAgent = computed(() => {
        return currentUser.value?.can_access_agent === true
    })

    // 是否是管理员（简化判定，后端可返回 is_admin 或角色包含 admin）
    const isAdmin = computed(() => {
        const user = currentUser.value as unknown as { is_admin?: boolean; roles?: unknown }
        if (!user) return false
        // 兼容不同后端字段
        if (user.is_admin === true) return true
        const roles = user.roles
        if (roles && typeof roles === 'object') {
            const roleObj = roles as { id?: number; name?: string; display_name?: string }
            return roleObj.name === 'admin' || roleObj.display_name === 'admin' || roleObj.id === 1
        }
        return false
    })

    // 用户角色信息
    const userRoles = computed(() => {
        return currentUser.value?.roles || null
    })

    // 是否有任何角色
    const hasRoles = computed(() => {
        return userRoles.value && userRoles.value.name
    })

    // 是否已设置密码（后端提供字段，兼容未返回时默认为true）
    const hasPassword = computed(() => {
        return currentUser.value?.has_password ?? true
    })

    // 用户显示名称
    const userDisplayName = computed(() => {
        return currentUser.value?.nickname || currentUser.value?.phone || '未知用户'
    })

    // ==================== 验证码倒计时功能 ====================

    /**
     * 获取验证码倒计时剩余时间
     * @param phone 手机号
     * @returns 剩余秒数
     */
    const getSMSCountdown = (phone: string): number => {
        return cacheStore.getCountdown(`sms:${phone}`)
    }

    /**
     * 检查是否正在验证码冷却期
     * @param phone 手机号
     */
    const isSMSCoolingDown = (phone: string): boolean => {
        return cacheStore.isCountingDown(`sms:${phone}`)
    }

    /**
     * 开始验证码倒计时
     * @param phone 手机号
     * @param duration 倒计时时长（秒），默认60秒
     */
    const startSMSCountdown = (phone: string, duration = 60): void => {
        cacheStore.startCountdown(`sms:${phone}`, duration)
    }

    /**
     * 清除验证码倒计时
     * @param phone 手机号
     */
    const clearSMSCountdown = (phone: string): void => {
        cacheStore.stopCountdown(`sms:${phone}`)
    }

    /**
     * 获取验证码倒计时详细信息
     * @param phone 手机号
     */
    const getSMSCountdownInfo = (phone: string) => {
        return cacheStore.getCountdownInfo(`sms:${phone}`)
    }

    // ==================== Token 管理 ====================

    /**
     * 保存JWT认证令牌
     * @param token JWT访问令牌
     */
    const saveAccessToken = (token: string): void => {
        accessToken.value = token
        localStorage.setItem('access_token', token)
        // 使用智能缓存保存到缓存系统
        cacheStore.setCache(cacheKeys.auth.token, token, cacheStore.getCacheTime('VERY_LONG'))
    }

    /**
     * 清除JWT认证令牌
     */
    const clearAccessToken = (): void => {
        accessToken.value = null
        localStorage.removeItem('access_token')
        cacheStore.removeCache(cacheKeys.auth.token)
    }

    /**
     * 从本地存储加载JWT令牌
     */
    const loadAccessToken = (): void => {
        // 请求拦截器也从 localStorage 取令牌，因此启动时必须使用同一数据源。
        // 真实令牌被 401 清除后，不能再从二级缓存恢复出一个“假登录”状态。
        const storedToken = localStorage.getItem('access_token')
        if (storedToken) {
            accessToken.value = storedToken
            cacheStore.setCache(cacheKeys.auth.token, storedToken, cacheStore.getCacheTime('VERY_LONG'))
            return
        }

        accessToken.value = null
        currentUser.value = null
        cacheStore.removeCache(cacheKeys.auth.token)
        cacheStore.removeCache(cacheKeys.auth.user)
    }

    // ==================== 用户信息管理 ====================

    /**
     * 加载用户信息
     * @param forceRefresh 是否强制刷新
     */
    const loadUserProfile = async (forceRefresh = false): Promise<UserDetail | null> => {
        try {
            const userData = await cacheStore.smartCache(
                cacheKeys.auth.user,
                () => userApi.getUserInfo(),
                forceRefresh
            )

            currentUser.value = userData
            return userData
        } catch (error) {
            console.error('加载用户信息失败:', error)
            authError.value = error instanceof Error ? error.message : '加载用户信息失败'
            return null
        }
    }

    /**
 * 更新用户信息
 * @param userData 用户数据
 */
    const updateUserInfo = async (userData: UserUpdateRequest): Promise<boolean> => {
        try {
            const result = await cacheStore.smartCache(
                'user:update',
                () => userApi.updateUserInfo(userData)
            )

            // 更新本地用户信息
            if (currentUser.value) {
                Object.assign(currentUser.value, result)
            }

            // 清除用户信息缓存，下次会重新加载
            cacheStore.removeCache(cacheKeys.auth.user)

            return true
        } catch (error) {
            console.error('更新用户信息失败:', error)
            authError.value = error instanceof Error ? error.message : '更新用户信息失败'
            return false
        }
    }

    /**
     * 清除所有认证信息
     */
    const clearUserAuth = (): void => {
        accessToken.value = null
        currentUser.value = null
        authError.value = null

        // 清除存储
        localStorage.removeItem('access_token')
        localStorage.removeItem('user')

        // 清除缓存
        cacheStore.clearCacheByPrefix('auth:')
        cacheStore.clearCacheByPrefix('user:')
    }

    // ==================== 登录相关方法 ====================

    /**
     * 密码登录
     * @param credentials 登录凭据
     */
    const loginWithPassword = async (credentials: UserLoginRequest): Promise<boolean> => {
        try {
            loginLoading.value = true
            authError.value = null

            const result = await cacheStore.smartCache(
                cacheKeys.auth.login,
                () => authApi.loginWithPassword(credentials)
            )

            if (result.access_token) {
                saveAccessToken(result.access_token)

                // JWT登录总是返回用户信息
                if (result.user) {
                    currentUser.value = result.user as UserDetail
                    cacheStore.setCache(cacheKeys.auth.user, result.user, cacheStore.getCacheTime('VERY_LONG'))
                }

                return true
            } else {
                authError.value = '登录失败：未返回访问令牌'
                return false
            }
        } catch (error) {
            console.error('密码登录失败:', error)
            // 安全地提取错误消息
            if (error && typeof error === 'object' && 'message' in (error as Record<string, unknown>)) {
                authError.value = (error as { message?: string }).message || '登录失败'
            } else if (typeof error === 'string') {
                authError.value = error
            } else {
                authError.value = '登录失败'
            }
            return false
        } finally {
            loginLoading.value = false
        }
    }

    /**
     * 短信登录（用户不存在时自动注册）
     * @param smsData 短信登录数据
     */
    const loginWithSMS = async (smsData: SMSLoginRequest): Promise<boolean> => {
        try {
            loginLoading.value = true
            authError.value = null

            const result = await cacheStore.smartCache(
                cacheKeys.auth.login,
                () => authApi.loginWithSMS(smsData)
            )

            if (result.access_token) {
                saveAccessToken(result.access_token)

                // 清除该手机号的验证码倒计时
                clearSMSCountdown(smsData.phone)

                // JWT登录总是返回用户信息
                if (result.user) {
                    currentUser.value = result.user as UserDetail
                    cacheStore.setCache(cacheKeys.auth.user, result.user, cacheStore.getCacheTime('VERY_LONG'))
                }

                return true
            } else {
                authError.value = '登录失败：未返回访问令牌'
                return false
            }
        } catch (error) {
            console.error('短信登录失败:', error)
            // 安全地提取错误消息
            if (error && typeof error === 'object' && 'message' in (error as Record<string, unknown>)) {
                authError.value = (error as { message?: string }).message || '短信登录失败'
            } else if (typeof error === 'string') {
                authError.value = error
            } else {
                authError.value = '登录失败'
            }
            return false
        } finally {
            loginLoading.value = false
        }
    }

    /**
     * 发送验证码
     * @param phone 手机号
     */
    const sendVerificationCode = async (phone: string): Promise<SendCodeResponse> => {
        try {
            // 检查是否在冷却期
            if (isSMSCoolingDown(phone)) {
                const remaining = getSMSCountdown(phone)
                const errorMsg = `请等待 ${remaining} 秒后再试`
                authError.value = errorMsg
                throw new Error(errorMsg)
            }

            authError.value = null

            const result = await cacheStore.smartCache(
                cacheKeys.auth.sendCode,
                () => authApi.sendCode({ phone })
            )

            // 开始60秒倒计时
            startSMSCountdown(phone, 60)
            return result

        } catch (error) {
            console.error('发送验证码失败:', error)
            // 安全地提取错误消息
            let errorMsg = '发送验证码失败'
            if (error && typeof error === 'object' && 'message' in (error as Record<string, unknown>)) {
                errorMsg = (error as { message?: string }).message || errorMsg
            } else if (typeof error === 'string') {
                errorMsg = error
            }

            authError.value = errorMsg
            // 重新抛出异常，让调用方能够捕获
            throw new Error(errorMsg)
        }
    }

    /**
     * 重置密码
     * @param resetData 重置密码数据
     */
    const resetPassword = async (resetData: PasswordResetRequest): Promise<boolean> => {
        try {
            authError.value = null

            await cacheStore.smartCache(
                cacheKeys.auth.resetPassword,
                () => authApi.resetPassword(resetData)
            )

            // 清除该手机号的验证码倒计时
            clearSMSCountdown(resetData.phone)

            return true
        } catch (error) {
            console.error('重置密码失败:', error)
            // 安全地提取错误消息
            if (error && typeof error === 'object' && 'message' in (error as Record<string, unknown>)) {
                authError.value = (error as { message?: string }).message || '重置密码失败'
            } else if (typeof error === 'string') {
                authError.value = error
            } else {
                authError.value = '重置密码失败'
            }
            return false
        }
    }

    /**
     * 登出 - JWT无状态，只需清除本地数据
     */
    const logout = (): void => {
        // JWT是无状态的，直接清除本地认证信息即可
        clearUserAuth()
    }

    // ==================== 认证状态检查 ====================

    /**
     * 检查认证状态
     */
    const checkAuthStatus = async (): Promise<boolean> => {
        try {
            // 如果没有JWT token，直接返回false
            if (!accessToken.value) {
                return false
            }

            // 尝试加载用户信息来验证token有效性
            const userInfo = await loadUserProfile(true)
            if (userInfo) return true

            // 真实 401 会先由响应拦截器移除 localStorage 令牌。这里再次核对，
            // 避免异步清理完成前仍用缓存用户判定为已登录。
            const storedToken = localStorage.getItem('access_token')
            if (!storedToken || storedToken !== accessToken.value) {
                clearUserAuth()
                return false
            }

            // Keep a cached session during temporary network failures. A real 401
            // clears both values in the response interceptor.
            return !!currentUser.value
        } catch (error) {
            console.error('检查认证状态失败:', error)
            // token无效，清除认证信息
            clearUserAuth()
            return false
        }
    }

    /**
     * 刷新认证状态
     */
    const refreshAuthStatus = async (): Promise<void> => {
        if (accessToken.value) {
            await loadUserProfile(true) // 强制刷新用户信息
        }
    }

    /**
 * 检查权限
 * @param permission 权限标识
 */
    const hasPermission = (): boolean => {
        if (!currentUser.value) return false
        // 管理员拥有所有权限
        if (isAdmin.value) return true
        // 这里暂时返回 true，后续接入精细权限时再完善
        return true
    }

    // ==================== 初始化 ====================

    /**
     * 初始化认证状态
     */
    const initAuth = async (): Promise<void> => {
        // 加载存储的JWT token
        loadAccessToken()

        if (accessToken.value) {
            const cachedUser = cacheStore.getCache<UserDetail>(cacheKeys.auth.user)
            if (cachedUser) {
                currentUser.value = cachedUser
            }
        }

        // 如果有token，尝试加载用户信息
        if (accessToken.value) {
            await checkAuthStatus()
        }
    }

    // ==================== 返回 API ====================

    return {
        // 状态
        accessToken: computed(() => accessToken.value),
        currentUser: computed(() => currentUser.value),
        loginLoading: computed(() => loginLoading.value),
        authError: computed(() => authError.value),

        // 计算属性
        isAuthenticated,
        canAccessAdmin,
        canAccessAgent,
        userRoles,
        hasRoles,
        userDisplayName,
        hasPassword,
        isAdmin,

        // 验证码倒计时
        getSMSCountdown,
        isSMSCoolingDown,
        startSMSCountdown,
        clearSMSCountdown,
        getSMSCountdownInfo,

        // JWT Token 管理
        saveAccessToken,
        clearAccessToken,
        loadAccessToken,

        // 用户信息管理
        loadUserProfile,
        updateUserInfo,
        clearUserAuth,

        // 登录相关
        loginWithPassword,
        loginWithSMS,
        sendVerificationCode,
        resetPassword,
        logout,

        // 认证状态检查
        checkAuthStatus,
        refreshAuthStatus,
        hasPermission,

        // 初始化
        initAuth
    }
}) 
